import { useCallback, useEffect, useState } from "react";
import { create } from "zustand";
import { api } from "@/lib/api";
import { authHeader } from "@/lib/aadhaar";
import { getPresent, indiaDate, isStudent } from "@/lib/classroom_live";
import { getStars } from "@/lib/classroom_quiz";
import { useLang } from "@/context/LanguageContext";

// [code, native name, English name, voice note]
export const CLASS_LANGS = [
  ["en", "English", "English"], ["hi", "हिंदी", "Hindi"], ["bn", "বাংলা", "Bengali"],
  ["pa", "ਪੰਜਾਬੀ", "Punjabi"], ["gu", "ગુજરાતી", "Gujarati"], ["mr", "मराठी", "Marathi"], ["te", "తెలుగు", "Telugu"],
  ["ta", "தமிழ்", "Tamil"], ["kn", "ಕನ್ನಡ", "Kannada"], ["ml", "മലയാളം", "Malayalam"],
];
export const langName = (code) => CLASS_LANGS.find((x) => x[0] === code)?.[2] || code;

const read = (k, d) => { try { return JSON.parse(localStorage.getItem(k) || "null") ?? d; } catch { return d; } };
const write = (k, v) => localStorage.setItem(k, JSON.stringify(v));
const slowNet = () => {
  const c = typeof navigator !== "undefined" && navigator.connection;
  return Boolean(c && (c.saveData || /(^|-)2g|3g/.test(c.effectiveType || "")));
};
const defaultRole = () => (localStorage.getItem("rs_teacher_token") ? "teacher" : "student");

// Class preferences shared by every classroom component: Zoya's language, low-data mode and who is asking.
export const useClassPrefs = create((set) => ({
  lang: read("rs_class_lang", null),
  lowbw: read("rs_lowbw", slowNet()),
  role: read("rs_class_role", null) || defaultRole(),
  setLang: (lang) => { write("rs_class_lang", lang); set({ lang }); },
  setLowbw: (lowbw) => { write("rs_lowbw", lowbw); set({ lowbw }); },
  setRole: (role) => { write("rs_class_role", role); set({ role }); },
}));

export const useClassLang = () => {
  const { lang } = useLang();
  const pick = useClassPrefs((s) => s.lang);
  return CLASS_LANGS.some((x) => x[0] === pick) ? pick : lang; // a saved language that was removed falls back to the site language
};

export const lessonSrc = (l, lowbw) => (lowbw ? l.src.replace(/\.mp4$/, "-480.mp4") : l.src);

// ---- guest progress kept on this device (signed-in students are tracked on the server) ----
const LABS = "rs_smart_labs";
const CH = "rs_smart_ch";
export const getLocalLabs = () => read(LABS, {});
export const saveLocalLab = (lab, rec) => {
  const all = getLocalLabs();
  const cur = all[lab] || {};
  const run = "success" in rec;
  const next = { ...cur, ...rec, attempts: (cur.attempts || 0) + (run ? 1 : 0), done: Boolean(cur.done || rec.success) };
  if (rec.success && !cur.done) next.done_at = new Date().toISOString();
  if (run && cur.done && !rec.success) Object.assign(next, { mistakes: cur.mistakes, checks_ok: cur.checks_ok, config: cur.config });
  write(LABS, { ...all, [lab]: next });
};
export const getLocalCh = () => read(CH, {});
export const saveLocalCh = (date, qid, correct, total, level) => {
  const all = getLocalCh();
  const day = all[date] || { answers: {}, level };
  if (day.answers[qid] === undefined) day.answers[qid] = correct;
  day.done = Object.keys(day.answers).length >= total;
  day.score = Object.values(day.answers).filter(Boolean).length;
  write(CH, { ...all, [date]: day });
  return day;
};

const shift = (iso, d) => new Date(new Date(`${iso}T00:00:00Z`).getTime() + d * 86400000).toISOString().slice(0, 10);
export const guestStats = () => {
  const ch = getLocalCh();
  const days = new Set(Object.keys(ch).filter((d) => ch[d].done));
  const today = indiaDate();
  let streak = 0;
  for (let d = days.has(today) ? today : shift(today, -1); days.has(d); d = shift(d, -1)) streak += 1;
  return {
    present: Object.keys(getPresent()).slice(0, 20), quiz: getStars(), watched: read("rs_classroom_watched", []).slice(0, 20),
    labs: getLocalLabs(), ch_correct: Object.values(ch).reduce((n, d) => n + Object.values(d.answers || {}).filter(Boolean).length, 0),
    ch_days: days.size, ch_streak: streak, ch_today: days.has(today),
  };
};

const studentAuth = () => (isStudent() ? authHeader("student") : undefined);

// Skill stage, adaptive level and learning path: from the server for students, from this device for guests.
export const useSmartProfile = () => {
  const [p, setP] = useState(null);
  const load = useCallback(() => {
    const req = isStudent() ? api.get("/student/smart/profile", authHeader("student")) : api.post("/smart/profile/guest", guestStats());
    return req.then((r) => setP(r.data)).catch(() => api.post("/smart/profile/guest", guestStats()).then((r) => setP(r.data)).catch(() => setP(false)));
  }, []);
  useEffect(() => { load(); }, [load]);
  return [p, load];
};

export const fetchLabs = (lang) => api.get(`/smart/labs?lang=${lang}`).then((r) => r.data);
export const saveLab = async (lab, rec) => {
  saveLocalLab(lab, rec);
  if (isStudent()) await api.post("/student/smart/lab", { lab, ...rec }, authHeader("student")).catch(() => {});
};
export const saveReflection = (lab, text) => {
  saveLocalLab(lab, { reflection: text });
  return isStudent() ? api.post("/student/smart/reflection", { lab, text }, authHeader("student")) : Promise.resolve();
};
export const fetchChallenge = (lang, level) => api.get(`/smart/challenge?lang=${lang}&level=${level}`, studentAuth()).then((r) => r.data);
export const answerChallenge = (lang, body) => api.post(`/smart/challenge/answer?lang=${lang}`, body, studentAuth()).then((r) => r.data);

// Doubt detection: a question that repeats most words of a recent one.
const words = (s) => new Set((s.toLowerCase().match(/[\p{L}\p{N}]{3,}/gu) || []));
export const isRepeat = (q, recent) => {
  const a = words(q);
  return a.size > 0 && recent.some((r) => {
    const b = words(r);
    const common = [...a].filter((w) => b.has(w)).length;
    return common / Math.max(1, Math.min(a.size, b.size)) >= 0.6;
  });
};
